import { useCloseRequest } from 'src/modules/shared/hooks/use-close-request';
import { CloseRequestContext } from 'src/modules/shared/contexts/close-request-context';
import React, { useEffect, useId, useRef } from 'react';
import { PiArrowUpRightLight, PiXBold } from 'react-icons/pi';
import type {
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import Loading from 'src/modules/shared/components/atoms/loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import { WebsiteTemplateMockPreview } from './website-template-mock-preview';

const SHEET_ACTION_CLASS =
  'flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-neutral-800 text-[15px] font-semibold text-white transition-colors hover:bg-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60';

/** Props for the full-screen phone preview. */
interface WebsitePreviewSheetProps {
  /** Whether the sheet is open. */
  open: boolean;
  /** Design shown in the preview. */
  template?: WebsiteTemplateRegistryTemplateSummary;
  /** Draft whose brand and contact values fill the preview. */
  form: WebsiteUpsertRequest;
  /** Preview-session URL of the draft; the mock preview shows without it. */
  sessionPreviewUrl?: string;
  /** Hostname shown in the desktop browser bar. */
  siteHost?: string;
  /** Whether publishing is unavailable right now. */
  publishDisabled: boolean;
  /** Whether a save or publish is running. */
  publishing: boolean;
  /** Whether opening a preview with real listings is unavailable. */
  realPreviewDisabled: boolean;
  /** Called when the user goes back to editing. */
  onClose: () => void;
  /** Called when the user publishes from the sheet. */
  onPublish: () => void;
  /** Called when the user opens the preview with real listings. */
  onOpenRealPreview: () => void;
}

/**
 * Full-screen preview for phones: the selected design in a phone frame, with
 * actions to keep editing or publish.
 *
 * Escape closes the sheet, and focus moves to the close button when it opens.
 *
 * @param props - Open state, preview inputs, and actions.
 * @returns The sheet, or nothing while closed.
 */
export const WebsitePreviewSheet: React.FC<WebsitePreviewSheetProps> = ({
  open,
  template,
  form,
  sessionPreviewUrl,
  siteHost,
  publishDisabled,
  publishing,
  realPreviewDisabled,
  onClose,
  onPublish,
  onOpenRealPreview,
}) => {
  const { t } = useI18n();
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const closeLayerId = useCloseRequest({ isOpen: open, onClose, modal: true });

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    closeButtonRef.current?.focus();
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  const templateName = template?.name ?? form.draftConfig.content.siteName;

  return (
    <CloseRequestContext.Provider value={closeLayerId}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-0 z-50 flex flex-col bg-neutral-900 text-white"
      >
        <div className="flex h-16 shrink-0 items-center justify-between gap-3 px-4">
          <Text as="h2" weight="semibold" className="truncate text-white">
            <span id={titleId}>
              {t('websites.settings.previewSheet.title', { templateName })}
            </span>
          </Text>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={t('websites.settings.previewSheet.close')}
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-neutral-700 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <PiXBold aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-6">
          <WebsiteTemplateMockPreview
            template={template}
            form={form}
            sessionPreviewUrl={sessionPreviewUrl}
            siteHost={siteHost}
            defaultDevice="mobile"
            tone="dark"
          />
          <Text size="small" className="mt-3 text-center text-neutral-300">
            {t('websites.settings.previewSheet.hint')}
          </Text>
          <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={onOpenRealPreview}
              disabled={realPreviewDisabled}
              className="inline-flex h-9 items-center gap-1 rounded-lg px-3 text-sm font-medium text-neutral-200 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t('websites.settings.previewSheet.realPreview')}
              <PiArrowUpRightLight aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex shrink-0 gap-2.5 border-t border-neutral-800 px-4 pb-5 pt-3">
          <button
            type="button"
            onClick={onClose}
            className={SHEET_ACTION_CLASS}
          >
            {t('websites.settings.previewSheet.keepEditing')}
          </button>
          <button
            type="button"
            onClick={onPublish}
            disabled={publishDisabled || publishing}
            className={classes(
              SHEET_ACTION_CLASS,
              'bg-white text-neutral-900 hover:bg-neutral-100'
            )}
          >
            {t('websites.settings.previewSheet.publish')}
            {publishing ? (
              <Loading inline size="xs" className="w-fit" variant="primary" />
            ) : null}
          </button>
        </div>
      </div>
    </CloseRequestContext.Provider>
  );
};
